// Conexões entre alunos — feature #2 (NÃO chamar de "LinkedIn", embora
// a inspiração seja parecida). Permite que aluno peça/aceite conexões e
// veja todas elas num só lugar pra conversar, compartilhar etc.
//
// Tabs:
//   - Ativas: conexões aceitas (cartões com ações: conversar, compartilhar, remover)
//   - Recebidos: pedidos pra mim aguardando resposta (aceitar/recusar)
//   - Enviados: pedidos que mandei aguardando resposta (cancelar)
//   - Histórico: removidos/recusados (read-only)

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Inbox,
  Loader2,
  Mail,
  MessageCircle,
  Plus,
  Send,
  Share2,
  Trash2,
  UserPlus,
  Users,
  X
} from "lucide-react";
import { call, ApiError, api } from "./api";
import { openChatWith } from "./PeerChatDock";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { getInitials } from "@app/ui/lib/display-name";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { banksData } from "./App";

interface ConnectionOther {
  userId: string;
  name: string;
  email: string;
  socialName: string | null;
  initialsOverride: string | null;
}

interface Connection {
  id: string;
  status: "pending" | "accepted" | "declined" | "removed";
  direction: "incoming" | "outgoing";
  other: ConnectionOther;
  note: string | null;
  requestedAt: string;
  respondedAt: string | null;
  removedAt: string | null;
}

type BoxKey = "active" | "pending-in" | "pending-out" | "history";

const BOX_META: Record<
  BoxKey,
  { label: string; icon: typeof Users; subtitle: string }
> = {
  active: { label: "Ativas", icon: Users, subtitle: "Suas conexões aceitas" },
  "pending-in": {
    label: "Recebidos",
    icon: Inbox,
    subtitle: "Pedidos aguardando sua resposta"
  },
  "pending-out": {
    label: "Enviados",
    icon: Send,
    subtitle: "Pedidos que você fez"
  },
  history: {
    label: "Histórico",
    icon: AlertCircle,
    subtitle: "Removidas e recusadas"
  }
};

interface State {
  /** Carrega cada box independentemente em paralelo no mount. */
  loading: Partial<Record<BoxKey, boolean>>;
  errors: Partial<Record<BoxKey, string>>;
  rows: Partial<Record<BoxKey, Connection[]>>;
  /** Estado de colapsado por seção. Default: 'history' colapsada. */
  collapsed: Partial<Record<BoxKey, boolean>>;
  /** Permite deeplink via URL ?tab=pending-in expandir/scrollar. */
  highlightedBox: BoxKey | null;
}

const state = signal<State>({
  loading: {},
  errors: {},
  rows: {},
  collapsed: { history: true },
  highlightedBox: null
});

async function loadBox(box: BoxKey, _force = false) {
  state.value = {
    ...state.value,
    loading: { ...state.value.loading, [box]: true },
    errors: { ...state.value.errors, [box]: undefined }
  };
  try {
    const res = await call<{ connections: Connection[] }>(
      `/api/student/connections?box=${encodeURIComponent(box)}`
    );
    state.value = {
      ...state.value,
      loading: { ...state.value.loading, [box]: false },
      rows: { ...state.value.rows, [box]: res.connections }
    };
  } catch (err) {
    state.value = {
      ...state.value,
      loading: { ...state.value.loading, [box]: false },
      errors: {
        ...state.value.errors,
        [box]: err instanceof Error ? err.message : String(err)
      }
    };
  }
}

/** Refresca TODOS os boxes — usado depois de uma ação que muda statuses. */
async function refreshActiveBox() {
  await Promise.all([
    loadBox("active", true),
    loadBox("pending-in", true),
    loadBox("pending-out", true),
    loadBox("history", true)
  ]);
}

function toggleCollapse(box: BoxKey) {
  state.value = {
    ...state.value,
    collapsed: {
      ...state.value.collapsed,
      [box]: !state.value.collapsed[box]
    }
  };
}

export function ConexoesView() {
  useEffect(() => {
    // Carrega TODOS os boxes em paralelo no mount.
    void Promise.all([
      loadBox("active"),
      loadBox("pending-in"),
      loadBox("pending-out"),
      loadBox("history")
    ]);
    // Deeplink: ?tab=pending-in expande e scrolla pra seção.
    try {
      const url = new URL(window.location.href);
      const tab = url.searchParams.get("tab") as BoxKey | null;
      if (tab && (tab === "active" || tab === "pending-in" || tab === "pending-out" || tab === "history")) {
        state.value = {
          ...state.value,
          collapsed: { ...state.value.collapsed, [tab]: false },
          highlightedBox: tab
        };
        // Scroll após render.
        setTimeout(() => {
          const el = document.getElementById(`conexoes-section-${tab}`);
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
          // Highlight expira após 3s
          setTimeout(() => {
            state.value = { ...state.value, highlightedBox: null };
          }, 3000);
        }, 200);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const [showAdd, setShowAdd] = useState(false);
  const s = state.value;

  return (
    <div class="space-y-4">
      <header class="flex items-center justify-between gap-4">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Users className="size-6 text-primary" />
            Conexões
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Conecte-se com outros alunos pra conversar e compartilhar materiais.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
        >
          <UserPlus className="size-4" />
          Nova conexão
        </button>
      </header>

      {/* Todas as seções visíveis ao mesmo tempo — colapsáveis. */}
      <ConnectionSection box="pending-in" />
      <ConnectionSection box="active" />
      <ConnectionSection box="pending-out" />
      <ConnectionSection box="history" />

      {showAdd && <AddConnectionModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function ConnectionSection({ box }: { box: BoxKey }) {
  const s = state.value;
  const meta = BOX_META[box];
  const Icon = meta.icon;
  const rows = s.rows[box] ?? [];
  const loading = !!s.loading[box];
  const error = s.errors[box];
  const collapsed = !!s.collapsed[box];
  const count = rows.length;
  const highlighted = s.highlightedBox === box;

  // Pending-in vazio = mostra mesmo assim por importância; outros colapsa
  // quando vazio + foi carregado pelo menos uma vez.
  const wasLoadedEmpty = !loading && count === 0 && error == null;
  const dim = wasLoadedEmpty && box !== "pending-in";

  return (
    <section
      id={`conexoes-section-${box}`}
      class={`rounded-lg border bg-card/40 transition-all ${
        highlighted
          ? "border-primary ring-2 ring-primary/30"
          : "border-border"
      } ${dim ? "opacity-70" : ""}`}
    >
      <button
        type="button"
        onClick={() => toggleCollapse(box)}
        class="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/30"
      >
        <Icon className={`size-4 shrink-0 ${box === "pending-in" && count > 0 ? "text-amber-500" : "text-primary"}`} />
        <div class="min-w-0 flex-1">
          <h2 class="flex items-center gap-2 text-sm font-bold">
            {meta.label}
            <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {count}
            </span>
            {box === "pending-in" && count > 0 && (
              <span class="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                aguarda resposta
              </span>
            )}
          </h2>
          <p class="mt-0.5 text-[11px] text-muted-foreground">{meta.subtitle}</p>
        </div>
        {collapsed ? (
          <ChevronRight className="size-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-4 text-muted-foreground" />
        )}
      </button>

      {!collapsed && (
        <div class="border-t border-border px-4 py-3">
          {loading && (
            <p class="flex items-center gap-2 py-3 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" /> Carregando…
            </p>
          )}
          {error && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              {error}
            </div>
          )}
          {!loading && !error && count === 0 && (
            <p class="py-3 text-center text-xs italic text-muted-foreground">
              {box === "active"
                ? "Você ainda não tem conexões. Use 'Nova conexão' pra começar."
                : box === "pending-in"
                ? "Nenhum pedido pendente."
                : box === "pending-out"
                ? "Sem pedidos enviados aguardando resposta."
                : "Sem histórico."}
            </p>
          )}
          {!loading && !error && count > 0 && (
            <div class="grid gap-3 sm:grid-cols-2">
              {rows.map((c) => (
                <ConnectionCard key={c.id} c={c} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ConnectionCard({ c }: { c: Connection }) {
  const initials = getInitials(c.other);
  const showName = c.other.name; // já vem com socialName aplicado (server)
  const [busy, setBusy] = useState(false);

  async function act(action: "accept" | "decline" | "cancel") {
    setBusy(true);
    try {
      await call(
        `/api/student/connections?id=${encodeURIComponent(c.id)}&action=${action}`,
        { method: "POST" }
      );
      await refreshActiveBox();
    } catch (err) {
      void openAlert({
        title: "Não foi possível concluir",
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const ok = await openConfirm({
      title: "Remover conexão?",
      message:
        `Se remover, ${showName} sairá das suas conexões. ` +
        "Pra reconectar, será necessário um novo pedido (de qualquer um dos dois lados). " +
        "Tem certeza?",
      confirmLabel: "Remover conexão",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await call(`/api/student/connections?id=${encodeURIComponent(c.id)}`, {
        method: "DELETE"
      });
      await refreshActiveBox();
    } catch (err) {
      void openAlert({
        title: "Não foi possível remover",
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="rounded-lg border border-border bg-card/60 p-4">
      <div class="flex items-start gap-3">
        <div class="grid size-12 shrink-0 place-items-center rounded-full bg-brand-gradient text-base font-bold uppercase text-white">
          {initials}
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold">{showName}</div>
          <div class="truncate text-[11px] text-muted-foreground">
            {c.other.email}
          </div>
          {c.note && (
            <div class="mt-1.5 rounded-md bg-muted/40 px-2 py-1 text-[11px] italic text-muted-foreground">
              "{c.note}"
            </div>
          )}
        </div>
      </div>

      {/* Ações por estado */}
      <div class="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
        {c.status === "accepted" && (
          <>
            <button
              type="button"
              onClick={() => openChatWith(c.other.userId, showName)}
              title="Abrir conversa"
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10"
            >
              <MessageCircle className="size-3.5" /> Conversar
            </button>
            <ShareWithConnectionButton email={c.other.email} name={showName} />
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              class="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/5 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-500/10 disabled:opacity-50"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
              Remover
            </button>
          </>
        )}
        {c.status === "pending" && c.direction === "incoming" && (
          <>
            <button
              type="button"
              onClick={() => act("decline")}
              disabled={busy}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
            >
              <X className="size-3.5" /> Recusar
            </button>
            <button
              type="button"
              onClick={() => act("accept")}
              disabled={busy}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
              Aceitar
            </button>
          </>
        )}
        {c.status === "pending" && c.direction === "outgoing" && (
          <>
            <span class="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              Aguardando resposta
            </span>
            <button
              type="button"
              onClick={() => act("cancel")}
              disabled={busy}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}
              Cancelar pedido
            </button>
          </>
        )}
        {(c.status === "declined" || c.status === "removed") && (
          <span class="text-[11px] text-muted-foreground">
            {c.status === "removed" ? "Conexão removida" : "Pedido recusado"}
          </span>
        )}
      </div>
    </div>
  );
}

/** Botão "Compartilhar" do card de conexão. Abre popover listando os bancos
 *  do próprio aluno; clicar em um dispara o convite de share usando a infra
 *  existente (POST /api/student/shares). Tudo inline, sem mudar de tela. */
function ShareWithConnectionButton({
  email,
  name
}: {
  email: string;
  name: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // Mostra TODOS os bancos visíveis pra este aluno. O backend valida quem
  // pode compartilhar o quê (dono / unlisted / public / admin de plataforma).
  // Admins enxergam bancos da org inteira e conseguem compartilhar tudo —
  // antes filtrávamos `!sharedByName` e isso escondia bancos legítimos.
  const ownBanks = banksData.value;

  async function share(bankId: string, bankName: string) {
    setBusy(bankId);
    try {
      const r = await api.createShare({
        kind: "bank",
        resourceId: bankId,
        recipientEmails: [email],
        message: `Compartilhado direto da sua conexão.`
      });
      setOpen(false);
      void openAlert({
        title: "Convite enviado",
        message: `${name} recebeu um convite pra acessar "${bankName}". Aceitando, o banco aparece na lista dele.`
      });
      // Para anti-enumeration, count pode ser 0 sem erro — mas tratamos como ok
      void r;
    } catch (err) {
      void openAlert({
        title: "Não consegui compartilhar",
        message: friendlyError(err)
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <span class="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={busy != null}
        title={
          ownBanks.length === 0
            ? "Você ainda não tem bancos próprios pra compartilhar"
            : "Compartilhar um dos seus bancos"
        }
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
      >
        {busy != null ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Share2 className="size-3.5" />
        )}
        Compartilhar
      </button>
      {open && (
        <div class="absolute right-0 top-9 z-30 max-h-72 w-64 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
          <div class="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Compartilhar com {name}
          </div>
          {ownBanks.length === 0 && (
            <p class="px-2 py-2 text-[11px] text-muted-foreground">
              Você ainda não tem bancos próprios pra compartilhar. Crie um
              no Editor ou peça pro professor te dar acesso.
            </p>
          )}
          {ownBanks.map((b) => (
            <button
              key={b.id}
              type="button"
              disabled={busy === b.id}
              onClick={() => share(b.id, b.name)}
              class="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent disabled:opacity-50"
            >
              <span class="min-w-0 flex-1 truncate">{b.name}</span>
              {busy === b.id && <Loader2 className="size-3 animate-spin shrink-0" />}
            </button>
          ))}
          {ownBanks.length > 0 && (
            <p class="mt-1 border-t border-border/50 px-2 pt-1 text-[10px] text-muted-foreground">
              Em breve: cadernos de IA e gravações.
            </p>
          )}
        </div>
      )}
    </span>
  );
}

function AddConnectionModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    if (!email.trim() || !email.includes("@")) {
      setErr("Digite um email válido.");
      return;
    }
    setSubmitting(true);
    try {
      await call("/api/student/connections", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          note: note.trim() || undefined
        })
      });
      // Recarrega "Enviados" pra mostrar o pedido novo. Como agora todas as
      // seções ficam visíveis, basta refrescar — expande "pending-out" caso
      // estivesse colapsada.
      await loadBox("pending-out", true);
      state.value = {
        ...state.value,
        collapsed: { ...state.value.collapsed, "pending-out": false },
        highlightedBox: "pending-out"
      };
      setTimeout(() => {
        state.value = { ...state.value, highlightedBox: null };
      }, 3000);
      onClose();
    } catch (e2) {
      // Mensagem amigável — segue padrão da memory feedback-user-friendly-errors
      if (e2 instanceof ApiError) {
        if (e2.code === "already_exists") {
          setErr(e2.message);
        } else if (e2.code === "user_not_found") {
          setErr("Esse email não está cadastrado na plataforma.");
        } else if (e2.code === "self_connect") {
          setErr("Você não pode se conectar com você mesmo.");
        } else {
          setErr(e2.message || "Não foi possível enviar o pedido agora.");
        }
      } else {
        setErr("Não foi possível enviar o pedido agora.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div class="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <UserPlus className="size-5 text-primary" /> Nova conexão
          </h2>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <form class="space-y-3" onSubmit={submit}>
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Email do colega
            </span>
            <div class="relative">
              <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                required
                value={email}
                onInput={(e) =>
                  setEmail((e.target as HTMLInputElement).value)
                }
                placeholder="colega@email.com"
                class="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary"
              />
            </div>
          </label>
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Mensagem (opcional)
            </span>
            <textarea
              value={note}
              maxLength={280}
              rows={3}
              onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
              placeholder="Diga oi, conte de onde se conhecem… (280 chars)"
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </label>
          {err && (
            <p class="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="size-3.5" /> {err}
            </p>
          )}
          <div class="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              <Plus className="size-3.5" />
              Enviar pedido
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
